import * as React from 'react';
import Grid from '@mui/material/Grid';
import { createUseStyles } from 'react-jss';
import { fieldTypes } from '../constants/app';
import { cssSpacingLg, formUi } from '../constants/globalcss';
import FormField from './FormField';
import { ILabel } from '../constants/interfaces';
import { getOptions, IFormStateValue } from '../utils/formUtils';

/**
 * Styles for the FormLayout component
 */
function isFullWidthField(label: ILabel): boolean {
  return (
    label.type === fieldTypes.childRowList ||
    label.type === fieldTypes.summation ||
    label.type === fieldTypes.table ||
    label.type === fieldTypes.label
  );
}

const useStyles = createUseStyles({
  centerContainer: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: formUi.spacing.sectionGap,
    margin: '0 auto',
    maxWidth: formUi.spacing.formColumnListMax,
    paddingInline: cssSpacingLg,
    width: '100%',
  },
  gridContent: {
    margin: '0 auto',
    maxWidth: formUi.spacing.formColumnGridMax,
    width: '100%',
  },
});

/**
 * Props for the FormLayout component
 */
export interface IFormLayoutProps {
  /** Array of visible form fields to render */
  visibleFields: ILabel[];
  /** Current form state */
  formState: Record<string, IFormStateValue>;
  /** Form validation errors */
  errors: Record<string, string>;
  /** Function to handle input changes */
  handleInputChange: (
    key: string | number,
    value: string | number | boolean,
    children?: unknown
  ) => void;
  /** Whether to use grid layout */
  shouldUseGrid: boolean;
  /** Reset trigger for form fields */
  resetTrigger: number;
  /** Whether the form contains table fields */
  hasTableField: boolean;
  /** Optional blur handler keyed by field label id */
  onFieldBlur?: (labelId: number, value: string) => void;
}

/**
 * FormLayout component that handles the rendering of form fields
 * in either grid or list layout based on configuration
 *
 * @param props - Component props
 * @returns JSX element containing the form layout
 */
export const FormLayout: React.FC<IFormLayoutProps> = React.memo(
  ({
    visibleFields,
    formState,
    errors,
    handleInputChange,
    shouldUseGrid,
    resetTrigger,
    hasTableField,
    onFieldBlur,
  }) => {
    const classes = useStyles();

    const getFieldBlurHandler = React.useCallback(
      (labelId: number, value: string) => {
        if (!onFieldBlur) {
          return undefined;
        }

        return () => onFieldBlur(labelId, value);
      },
      [onFieldBlur]
    );

    /**
     * Memoized form fields to prevent unnecessary re-renders
     */
    const renderFields = React.useMemo(
      () =>
        visibleFields.map((label) => (
          <FormField
            key={label.labelId}
            value={String(formState[label.labelId]?.value || '')}
            handleInputChange={handleInputChange}
            errorText={errors[label.labelId]}
            field={{ ...label, options: getOptions(label.options, formState) }}
            formStateForVisibility={formState}
            isInGrid={shouldUseGrid}
            resetTrigger={resetTrigger}
            hasTableField={hasTableField}
            onBlur={getFieldBlurHandler(
              label.labelId,
              String(formState[label.labelId]?.value || '')
            )}
          />
        )),
      [
        visibleFields,
        formState,
        errors,
        handleInputChange,
        shouldUseGrid,
        resetTrigger,
        hasTableField,
        getFieldBlurHandler,
      ]
    );

    /**
     * Render the appropriate layout based on shouldUseGrid flag
     */
    if (shouldUseGrid) {
      return (
        <div className={classes.gridContent}>
          <Grid columnSpacing={{ md: 4, xs: 2 }} container rowSpacing={2.5}>
            {visibleFields.map((label) => (
              <Grid
                key={label.labelId}
                size={isFullWidthField(label) ? { xs: 12 } : { xs: 12, md: 6 }}
              >
                <FormField
                  value={String(formState[label.labelId]?.value || '')}
                  handleInputChange={handleInputChange}
                  errorText={errors[label.labelId]}
                  field={{
                    ...label,
                    options: getOptions(label.options, formState),
                  }}
                  formStateForVisibility={formState}
                  isInGrid={shouldUseGrid}
                  resetTrigger={resetTrigger}
                  hasTableField={hasTableField}
                  onBlur={getFieldBlurHandler(
                    label.labelId,
                    String(formState[label.labelId]?.value || '')
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      );
    }

    return <div className={classes.centerContainer}>{renderFields}</div>;
  }
);

FormLayout.displayName = 'FormLayout';
